import { useCallback, useEffect, useRef } from "react";
import type { ChallengeHistoryItem } from "../../challenges/types/challengeAdmin";
import type { ChallengeModalMode } from "../../challenges/types/challengeDraft";
import type { AdminTab } from "../types/admin";

type UseAdminChallengeRouteSelectionParams = {
  challengeIdFromRoute?: number;
  challengeRouteMode?: ChallengeModalMode;
  goToAdminListPage: () => void;
  loading: boolean;
  onSelectChallenge: (
    id: number,
    routeMode?: ChallengeModalMode,
  ) => Promise<boolean>;
  selectTab: (nextTab: AdminTab) => void;
  setShowChallengeModal: (next: boolean) => void;
  token?: string | null;
};

function getChallengeRoute(item: ChallengeHistoryItem) {
  const isTournament =
    String(item.challengeType || "")
      .trim()
      .toLowerCase() === "tournament";
  const basePath = isTournament ? "torneio" : "desafio";
  const query = new URLSearchParams();
  const challengeName = String(item.challengeName || item.groupName || "").trim();
  if (challengeName) query.set("name", challengeName);
  if (item.status) query.set("status", item.status);
  if (item.challengeType) query.set("type", item.challengeType);
  if (Number.isFinite(item.participantsCount)) {
    query.set("participants", String(item.participantsCount));
  }
  if (item.scheduledAt) query.set("scheduledAt", item.scheduledAt);
  const querySuffix = query.toString();
  return `#/admin/${basePath}/${item.id}${
    querySuffix ? `?${querySuffix}` : ""
  }`;
}

export function useAdminChallengeRouteSelection({
  challengeIdFromRoute,
  challengeRouteMode,
  goToAdminListPage,
  loading,
  onSelectChallenge,
  selectTab,
  setShowChallengeModal,
  token,
}: UseAdminChallengeRouteSelectionParams) {
  const openedRouteChallengeIdRef = useRef<number | null>(null);

  const openChallengeRoute = useCallback(
    (item: ChallengeHistoryItem) => {
      const nextHash = getChallengeRoute(item);
      const nextMode: ChallengeModalMode = "edit";

      if (window.location.hash === nextHash) {
        void onSelectChallenge(item.id, nextMode);
        return;
      }

      window.location.hash = nextHash;
    },
    [onSelectChallenge],
  );

  useEffect(() => {
    if (!challengeIdFromRoute) {
      openedRouteChallengeIdRef.current = null;
      setShowChallengeModal(false);
      return;
    }
    selectTab("history");
    if (loading) return;
    if (!token) {
      goToAdminListPage();
      return;
    }
    if (openedRouteChallengeIdRef.current === challengeIdFromRoute) return;
    openedRouteChallengeIdRef.current = challengeIdFromRoute;
    void onSelectChallenge(challengeIdFromRoute, challengeRouteMode).then(
      (ok) => {
        if (!ok) {
          goToAdminListPage();
        }
      },
    );
  }, [
    challengeIdFromRoute,
    challengeRouteMode,
    goToAdminListPage,
    loading,
    onSelectChallenge,
    selectTab,
    setShowChallengeModal,
    token,
  ]);

  const onCloseChallengeModal = useCallback(() => {
    if (challengeIdFromRoute) {
      setShowChallengeModal(false);
      goToAdminListPage();
      return;
    }
    setShowChallengeModal(false);
  }, [challengeIdFromRoute, goToAdminListPage, setShowChallengeModal]);

  return {
    onCloseChallengeModal,
    openChallengeRoute,
  };
}
